import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import OTPInput from "react-otp-input";
import SEO from "../../components/seo";
import LayoutTwo from "../../layouts/LayoutTwo";
import authImage from "../../assets/images/Auth/auth_bg.png";
import InlineSpinner from "../../components/loading/InlineSpinner";
import LoadingScreen from "../../components/loading/LoadingScreen";
import { apiInstance } from "../../helpers/axios";
import { useFetchData } from "../../helpers/api";
import { handleGlobalInputChange } from "../../helpers/functions";
import {
  RegisterFormInputData,
  RegisterInputData,
  requiredFields,
} from "./data";
import toast from "react-hot-toast";

const apiUrl = "/Customer/SignupWithOTP";
const verifyOtpUrl = "/Customer/VerifySignup";

const Register = () => {
  const [formData, setFormData] = useState(RegisterFormInputData);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [errorMassage, setErrorMassage] = useState({});
  const [loading, setLoading] = useState(false);

  const { data: countryData, isLoadingCountries } = useFetchData(
    ["registerCountryData"],
    "/Customer/GetCountry",
    {}
  );
  const { data: stateData, isLoadingState } = useFetchData(
    ["registerStateData", formData.ShippingCountry],
    "/Customer/GetState",
    { CountryId: formData.ShippingCountry }
  );
  const { data: stateDataforBiliing, isLoadingStateBilling } = useFetchData(
    ["registerStateBillingData", formData.BillingCountry],
    "/Customer/GetState",
    { CountryId: formData.BillingCountry }
  );
  const { data: cityData, isLoadingCities } = useFetchData(
    ["registerCityData"],
    "/Customer/getCity",
    {}
  );

  // set resend login otp timer
  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendTimer]);

  useEffect(() => {
    if ((stateData?.Data?.length && stateDataforBiliing?.Data?.length) > 0) {
      setFormData((prev) => ({
        ...prev,
        ShippingState: stateData?.Data[0].StateId,
        BillingState: stateDataforBiliing?.Data[0].StateId,
      }));
    }
  }, [stateData, stateDataforBiliing]);

  const handleInputChange = (e) => {
    setShowOtpInput(false);
    const { name, value } = e.target;
    handleGlobalInputChange(e, setFormData);

    setErrorMassage((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (value.trim() !== "") {
        delete updatedErrors[name];
      }
      return updatedErrors;
    });
  };

  // input fields errors check logic
  const checkInputFields = () => {
    const errors = {};

    Object.keys(formData).forEach((field) => {
      const fieldValue = formData[field];

      if (requiredFields.includes(field)) {
        if (fieldValue == null || fieldValue === "") {
          errors[field] = "This Field Is Required.";
        } else if (typeof fieldValue === "string" && fieldValue.trim() === "") {
          errors[field] = "This Field Is Required.";
        } else if (typeof fieldValue === "number" && fieldValue === 0) {
          errors[field] = "This Field Is Required.";
        }
      }

      if (field === "Email" && fieldValue) {
        const emailValue = String(fieldValue).trim();
        if (!emailValue.includes("@")) {
          errors.Email = "Email must contain '@'.";
        } else {
          const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
          if (!emailRegex.test(emailValue)) {
            errors.Email = "Please enter a valid email address.";
          }
        }
      }
    });

    return errors;
  };

  // send otp api
  const sendOTP = (e) => {
    e.preventDefault();
    setErrorMassage({});

    const errors = checkInputFields();
    if (Object.keys(errors).length > 0) {
      setErrorMassage(errors);
      return;
    }

    setLoading(true);

    const { FirstName, LastName, Mobile, Email, type } = formData;
    const data = {
      FirstName,
      LastName,
      WhatsappNo: Mobile,
      username: Email,
      type,
    };

    apiInstance
      .post(apiUrl, data)
      .then((response) => {
        const data = response.data;
        if (data.Status === true) {
          toast.success(data.Message);
          setTimeout(() => {
            setShowOtpInput(true);
            setResendTimer(15);
          }, 1000);
        } else {
          toast.error(data.Message);
        }
      })
      .catch(() => toast.error("Error occurred while sending OTP"))
      .finally(() => setLoading(false));
  };

  // verify otp and create new customer
  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMassage({});

    if (formData.otp.length !== 6) {
      toast.error("Please enter a valid OTP.");
      return;
    }

    const errors = checkInputFields();
    if (Object.keys(errors).length > 0) {
      setErrorMassage(errors);
      return;
    }

    setLoading(true);

    const updatedFormData = { ...formData };

    if (updatedFormData.WhatsappNo === "") {
      updatedFormData.WhatsappNo = updatedFormData.Mobile;
    }

    const billingCountryName =
      countryData?.Data?.find(
        (country) =>
          country.CountryId === Number(updatedFormData.BillingCountry)
      )?.CountryName || "";
    const billingStateName =
      stateData?.Data?.find(
        (state) => state.StateId === Number(updatedFormData.BillingState)
      )?.StateName || "";
    const shippingCountryName =
      countryData?.Data?.find(
        (country) =>
          country.CountryId === Number(updatedFormData.ShippingCountry)
      )?.CountryName || "";
    const shippingStateName =
      stateData?.Data?.find(
        (state) => state.StateId === Number(updatedFormData.ShippingState)
      )?.StateName || "";

    const {
      NumberCheckbox,
      BillingAddress,
      BillingCity,
      BillingState,
      BillingCountry,
      BillingZipCode,
      ShippingAddress,
      ShippingCity,
      ShippingState,
      ShippingCountry,
      ShippingZipCode,
      SameAsBilling,
      ...rest
    } = updatedFormData;

    const apiData = {
      ...rest,
      Status: "Deactivate",
      Billing: {
        Address: BillingAddress,
        City: BillingCity,
        State: billingStateName,
        Country: billingCountryName,
        ZipCode: BillingZipCode,
      },
      Shipping: {
        Address: ShippingAddress,
        City: ShippingCity,
        State: shippingStateName,
        Country: shippingCountryName,
        ZipCode: ShippingZipCode,
        SameAsBilling: SameAsBilling,
      },
    };

    apiInstance
      .post(verifyOtpUrl, apiData)
      .then((response) => {
        const data = response.data;
        if (data.Status === true) {
          toast.success(data.Message);
          resetLoginForm();
        } else {
          toast.error(data.Message);
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error("Error occured while creating user");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // reset form function
  const resetLoginForm = () => {
    setFormData(RegisterFormInputData);
    setShowOtpInput(false);
  };

  return (
    <Fragment>
      <SEO titleTemplate="Register" />
      <LayoutTwo>
        {isLoadingCountries ||
        isLoadingState ||
        isLoadingStateBilling ||
        isLoadingCities ? (
          <LoadingScreen />
        ) : (
          <div className="container">
            <div className="auth_con sign_up">
              <div className="content_con">
                <h1 className="heading">Sign Up</h1>
                <p className="desc">
                  Please enter Following Details to register
                </p>
                <div>
                  <div className="input_grid">
                    {RegisterInputData.map((input, index) => (
                      <Fragment key={index}>
                        <div
                          className={`auth_${input.type || "text"} ${
                            errorMassage[input.name] ? "input_error" : ""
                          }`}
                        >
                          {input.type === "textarea" ? (
                            <textarea
                              name={input.name}
                              placeholder={input.label}
                              disabled={loading}
                              autoComplete="off"
                              rows="3"
                              value={formData[input.name]}
                              onChange={(e) =>
                                handleInputChange({
                                  target: {
                                    name: input.name,
                                    value: e.target.value.toUpperCase(),
                                  },
                                })
                              }
                              style={{
                                color: "black",
                              }}
                            />
                          ) : (
                            <input
                              name={input.name}
                              type={input.type || "text"}
                              placeholder={input.label}
                              disabled={loading}
                              autoComplete="off"
                              value={formData[input.name]}
                              onChange={(e) =>
                                handleInputChange({
                                  target: {
                                    name: input.name,
                                    value: e.target.value.toUpperCase(),
                                  },
                                })
                              }
                              pattern={input.pattern}
                              title={input.title}
                              style={{
                                color: "black",
                              }}
                            />
                          )}
                          {/* Show error message for each field */}
                          {errorMassage[input.name] && (
                            <p
                              style={{
                                color: "red",
                                marginTop: "8px",
                                transition:
                                  "opacity 0.5s ease, transform 0.5s ease",
                                fontSmooth: "2em",
                                height: "5px",
                              }}
                            >
                              {errorMassage[input.name]}
                            </p>
                          )}
                        </div>

                        {/* WhatsApp number checkbox */}
                        {index === 3 && (
                          <>
                            <div
                              className={`auth_number checkbox_hidden_input mobile ${
                                formData.NumberCheckbox ? "hide" : "show"
                              }`}
                            >
                              <input
                                name="WhatsappNo"
                                type="number"
                                placeholder="Whatsapp Number"
                                disabled={loading}
                                autoComplete="off"
                                value={formData.WhatsappNo}
                                onChange={handleInputChange}
                                style={{ color: "black" }}
                              />
                            </div>
                            <div className="custom_block">
                              <input
                                type="checkbox"
                                name="NumberCheckbox"
                                id="register_whatsapp_checkbox"
                                checked={formData.NumberCheckbox}
                                onChange={handleInputChange}
                                style={{ color: "black" }}
                                disabled={loading}
                              />
                              <label htmlFor="register_whatsapp_checkbox">
                                WhatsApp Same as Phone Number
                              </label>
                            </div>
                          </>
                        )}

                        {index === 5 && (
                          <>
                            <div className="auth_select">
                              <label htmlFor="register_shipping_country">
                                Shipping Country
                              </label>
                              <select
                                name="ShippingCountry"
                                id="register_shipping_country"
                                value={formData.ShippingCountry}
                                onChange={handleInputChange}
                                disabled={loading}
                              >
                                {countryData?.Data &&
                                countryData.Data.length > 0 ? (
                                  countryData.Data.map((item, index) => (
                                    <option key={index} value={item.CountryId}>
                                      {item.CountryName}
                                    </option>
                                  ))
                                ) : (
                                  <option value="">
                                    No countries available
                                  </option>
                                )}
                              </select>
                            </div>
                            <div className="auth_select">
                              <label htmlFor="register_shipping_state">
                                Shipping State
                              </label>
                              <select
                                name="ShippingState"
                                id="register_shipping_state"
                                value={formData.ShippingState}
                                onChange={handleInputChange}
                                disabled={loading}
                              >
                                {stateData?.Data &&
                                stateData.Data.length > 0 ? (
                                  stateData.Data.map((item, index) => (
                                    <option key={index} value={item.StateId}>
                                      {item.StateName}
                                    </option>
                                  ))
                                ) : (
                                  <option value="">No states available</option>
                                )}
                              </select>
                            </div>
                            <div className="auth_select">
                              <label htmlFor="register_shipping_city">
                                Shipping City
                              </label>
                              <select
                                name="ShippingCity"
                                id="register_shipping_city"
                                value={formData.ShippingCity}
                                onChange={handleInputChange}
                                disabled={loading}
                              >
                                <option value="00" disabled>
                                  Select City
                                </option>
                                {cityData?.Data && cityData.Data.length > 0 ? (
                                  cityData.Data.map((item, index) => (
                                    <option key={index} value={item.CityName}>
                                      {item.CityName}
                                    </option>
                                  ))
                                ) : (
                                  <option value="">No city available</option>
                                )}
                              </select>
                            </div>
                          </>
                        )}

                        {/* Shipping Address checkbox */}
                        {index === 6 && (
                          <>
                            <div className="custom_block">
                              <input
                                type="checkbox"
                                name="SameAsBilling"
                                id="register_sameaddress_checkbox"
                                checked={formData.SameAsBilling}
                                onChange={handleInputChange}
                                disabled={loading}
                              />
                              <label htmlFor="register_sameaddress_checkbox">
                                Billing Address same as Shipping address
                              </label>
                            </div>

                            <div
                              className={`auth_textarea checkbox_hidden_input address ${
                                formData.SameAsBilling ? "hide" : "show"
                              }`}
                            >
                              <textarea
                                rows="3"
                                name="BillingAddress"
                                placeholder="Billing Address"
                                disabled={loading}
                                autoComplete="off"
                                value={formData.BillingAddress}
                                onChange={(e) =>
                                  handleInputChange({
                                    target: {
                                      name: e.target.name,
                                      value: e.target.value.toUpperCase(),
                                    },
                                  })
                                }
                              />
                              <div className="address_inner_grid">
                                <div className="auth_select">
                                  <label htmlFor="register_billing_country">
                                    Billing Country
                                  </label>
                                  <select
                                    name="BillingCountry"
                                    id="register_billing_country"
                                    value={formData.BillingCountry}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                  >
                                    {countryData?.Data &&
                                    countryData.Data.length > 0 ? (
                                      countryData.Data.map((item, index) => (
                                        <option
                                          key={index}
                                          value={item.CountryId}
                                        >
                                          {item.CountryName}
                                        </option>
                                      ))
                                    ) : (
                                      <option value="">
                                        No countries available
                                      </option>
                                    )}
                                  </select>
                                </div>
                                <div className="auth_select">
                                  <label htmlFor="register_billing_state">
                                    Billing State
                                  </label>
                                  <select
                                    name="BillingState"
                                    id="register_billing_state"
                                    value={formData.BillingState}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                  >
                                    {stateDataforBiliing?.Data &&
                                    stateDataforBiliing.Data.length > 0 ? (
                                      stateDataforBiliing.Data.map(
                                        (item, index) => (
                                          <option
                                            key={index}
                                            value={item.StateId}
                                          >
                                            {item.StateName}
                                          </option>
                                        )
                                      )
                                    ) : (
                                      <option value="">
                                        No states available
                                      </option>
                                    )}
                                  </select>
                                </div>

                                <div className="auth_select">
                                  <label htmlFor="register_billing_city">
                                    Billing City
                                  </label>
                                  <select
                                    name="BillingCity"
                                    id="register_billing_city"
                                    value={formData.BillingCity}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                  >
                                    <option value="00" disabled>
                                      Select City
                                    </option>
                                    {cityData?.Data &&
                                    cityData.Data.length > 0 ? (
                                      cityData.Data.map((item, index) => (
                                        <option
                                          key={index}
                                          value={item.CityName}
                                        >
                                          {item.CityName}
                                        </option>
                                      ))
                                    ) : (
                                      <option value="">
                                        No city available
                                      </option>
                                    )}
                                  </select>
                                </div>

                                <input
                                  name="BillingZipCode"
                                  type="number"
                                  placeholder="Billing ZipCode"
                                  disabled={loading}
                                  autoComplete="off"
                                  value={formData.BillingZipCode}
                                  onChange={handleInputChange}
                                />
                              </div>
                              {errorMassage[input.name] && (
                                <p
                                  style={{
                                    opacity: errorMassage[input.name] ? 1 : 0,
                                    transform: errorMassage[input.name]
                                      ? "translateY(0)"
                                      : "translateY(-10px)",
                                    color: "red",
                                    marginTop: "8px",
                                    transition:
                                      "opacity 0.5s ease, transform 0.5s ease",
                                    fontSmooth: "2em",
                                    height: "5px",
                                  }}
                                >
                                  {errorMassage[input.name]}
                                </p>
                              )}
                            </div>
                          </>
                        )}
                      </Fragment>
                    ))}
                  </div>

                  <div className="login_type_con">
                    <label className="main_label">Receive OTP via:</label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="type"
                        value="email"
                        checked={formData.type === "email"}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                      <span>Email</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="type"
                        value="wa"
                        checked={formData.type === "wa"}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                      <span>WhatsApp</span>
                    </label>
                  </div>

                  {/* OTP Input Boxes */}
                  <div
                    className={`otp_input_container ${
                      showOtpInput ? "show" : ""
                    }`}
                  >
                    <div className="otp_input_inner">
                      <label htmlFor="login_email">OTP</label>
                      <OTPInput
                        value={formData.otp}
                        onChange={(otp) =>
                          setFormData((prev) => ({ ...prev, otp }))
                        }
                        numInputs={6}
                        inputType="number"
                        renderInput={(props) => (
                          <input {...props} disabled={loading} />
                        )}
                      />
                      <div className="otp_btn_con">
                        <span
                          onClick={resendTimer === 0 ? sendOTP : null}
                          className={`resend_btn ${
                            resendTimer > 0 ? "disabled" : ""
                          }`}
                        >
                          {resendTimer > 0
                            ? `Resend OTP in ${resendTimer}s`
                            : "Resend OTP"}
                        </span>
                        <span className="reset_btn" onClick={resetLoginForm}>
                          Reset Form
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Submit button */}
                  <button
                    className="button primary_btn submit_btn"
                    disabled={loading}
                    onClick={showOtpInput ? handleSubmit : sendOTP}
                  >
                    {loading ? (
                      <InlineSpinner />
                    ) : showOtpInput ? (
                      "Verify OTP"
                    ) : (
                      "Send OTP"
                    )}
                  </button>
                </div>
                <div className="divider_div">
                  <span>OR</span>
                  <span></span>
                </div>
                <div className="auth_login_link">
                  Already Registered? <Link to="/login">Login</Link>
                </div>
              </div>
              <div className="banner_con">
                <img src={authImage} alt="register" />
              </div>
            </div>
          </div>
        )}
      </LayoutTwo>
    </Fragment>
  );
};

export default Register;
